"use client";

import { useState, useEffect } from "react";
import { getApiBaseUrl } from '@/utils/api-config';

interface DeploymentStatusProps {
  className?: string;
}

type DeploymentStatus = "checking" | "deploying" | "idle" | "error";

export default function DeploymentStatus({
  className = "",
}: DeploymentStatusProps) {
  const [status, setStatus] = useState<DeploymentStatus>("checking");
  const [buildId, setBuildId] = useState<string>("unknown");
  const [costs, setCosts] = useState<{ aws: number; ai: number }>({ aws: 0, ai: 0 });
  const [costsLoading, setCostsLoading] = useState(true);

  useEffect(() => {
    // Get current build ID from environment - fail fast if missing
    const currentBuildId = process.env.NEXT_PUBLIC_BUILD_ID;
    if (!currentBuildId) {
      throw new Error('CRITICAL: NEXT_PUBLIC_BUILD_ID environment variable is required but not set');
    }
    if (currentBuildId === "HEAD") {
      throw new Error('CRITICAL: NEXT_PUBLIC_BUILD_ID should not be "HEAD" - build configuration error');
    }
    setBuildId(currentBuildId);

    const checkDeploymentStatus = async () => {
      try {
        const apiUrl = await getApiBaseUrl();
        const isLocalDev = apiUrl?.includes('localhost');
        
        if (isLocalDev) {
          // Skip deployment status checks for local development
          setStatus("idle");
          setBuildId("local-dev");
          return;
        }

        // Call build status service through API Gateway
        const response = await fetch(`${apiUrl}/deployment/status`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const data = await response.json();
          setStatus(data.is_deploying ? "deploying" : "idle");

          // Update build ID with actual commit ID from deployment if available
          if (data.last_job?.commit_id && data.last_job.commit_id !== "HEAD") {
            setBuildId(data.last_job.commit_id.substring(0, 7));
          }
        } else {
          console.warn("Deployment status API returned:", response.status);
          setStatus("idle"); // Default to idle instead of error
        }
      } catch (error) {
        console.warn("Could not check deployment status:", error);
        setStatus("idle"); // Default to idle instead of error to not alarm users
      }
    };

    // Check immediately
    checkDeploymentStatus();

    // Check every 10 seconds
    const interval = setInterval(checkDeploymentStatus, 10000);

    return () => clearInterval(interval);
  }, []);

  // Fetch costs every 30 seconds
  useEffect(() => {
    const fetchCosts = async () => {
      try {
        const apiUrl = await getApiBaseUrl();
        const isLocalDev = apiUrl?.includes('localhost');
        
        if (isLocalDev) {
          setCosts({ aws: 0, ai: 0 });
          setCostsLoading(false);
          return;
        }
        
        const response = await fetch(`${apiUrl}/jobs/metrics/costs`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          setCosts({
            aws: data.costs?.aws?.total_dollars || 0,
            ai: data.costs?.openai?.total_dollars || 0
          });
          setCostsLoading(false);
        } else {
          console.warn("Cost metrics API returned:", response.status);
          setCostsLoading(false);
        }
      } catch (error) {
        console.warn("Could not fetch cost metrics:", error);
        setCostsLoading(false);
      }
    };
    
    // Fetch immediately
    fetchCosts();
    
    // Fetch every 30 seconds
    const interval = setInterval(fetchCosts, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = () => {
    switch (status) {
      case "checking":
        return "bg-gray-400";
      case "deploying":
        return "bg-yellow-400 animate-pulse";
      case "idle":
        return "bg-green-400";
      case "error":
        return "bg-red-400";
      default:
        return "bg-gray-400";
    }
  };

  const getStatusTitle = () => {
    switch (status) {
      case "checking":
        return "Checking deployment status...";
      case "deploying":
        return "Deployment in progress";
      case "idle":
        return `Deployment idle (build: ${buildId})`;
      case "error":
        return "Could not check deployment status";
      default:
        return "Unknown status";
    }
  };

  return (
    <div
      className={`flex items-center space-x-2 ${className}`}
      title={getStatusTitle()}
    >
      <div className={`w-3 h-3 rounded-full ${getStatusColor()}`} />
      <span className="text-xs text-gray-500">{buildId}</span>
      {!costsLoading && (costs.aws > 0 || costs.ai > 0) && (
        <>
          <span className="text-xs text-gray-400">|</span>
          <span className="text-xs text-gray-500" title="AWS infrastructure costs (month-to-date)">
            AWS: ${costs.aws.toFixed(2)}
          </span>
          <span className="text-xs text-gray-400">|</span>
          <span className="text-xs text-gray-500" title="OpenAI API costs (today)">
            AI: ${costs.ai.toFixed(2)}
          </span>
        </>
      )}
    </div>
  );
}